import React, { useState } from "react";
import EntriesDrawer from "./EntriesDrawer";
import { toQuery, useAnalytics } from "./useAnalytics";
import { periodBounds } from "./usePeriod";

const DIMENSIONS = [
  { value: "time_code", label: "Time code" },
  { value: "employee", label: "Employee" },
  { value: "client", label: "Client" },
  { value: "group", label: "Group" },
  { value: "task", label: "Task" },
  { value: "month", label: "Month" },
];
const MEASURES = [
  { value: "hours", label: "Hours" },
  { value: "amount", label: "Amount" },
  { value: "entries", label: "Entries" },
];

// A pivot dimension -> the entries filter it maps to. "month" is
// deliberately absent: it contributes a date range instead of a filter
// parameter (see monthAxisBounds below). Unlike PersonView's own
// FILTER_FOR, this one includes "employee" -- PersonView never offers an
// employee dimension (it is always scoped to one person already), so
// there is nothing to mirror there for that entry.
const FILTER_FOR = { time_code: "time_code", employee: "employee",
                     client: "client", group: "group", task: "task" };

// pivot's employee axis can emit an "(unassigned)" bucket -- TimeEntry.
// created_by is null=True -- whose identity in row_ids/col_ids is null.
// filtered_entries has no unassigned-employee sentinel (unlike
// "(no client)"/"(no code)"/"(no task)", which round-trip as literal
// placeholder strings), so sending employee=(unassigned) 400s. Cells for
// that bucket are rendered as plain text instead of a dead/erroring
// button -- same call TeamTab.jsx makes for its own "(unassigned)" row.
function isUnassignedEmployee(dim, label, ids) {
  return dim === "employee" && ids && ids[label] === null;
}

const cell = { padding: "8px 10px", textAlign: "right",
               borderBottom: "1px solid #f3f4f6" };
const head = { ...cell, fontWeight: 600, textAlign: "right",
               borderBottom: "2px solid #e5e7eb" };
const cellButton = {
  background: "none", border: "none", padding: 0, font: "inherit",
  color: "inherit", cursor: "pointer", width: "100%", textAlign: "right",
};

// ISO date strings ("YYYY-MM-DD") compare correctly with plain > and <,
// but NOT with Math.max/Math.min -- those coerce to Number first, which
// turns a date string into NaN. "Take the later/earlier of the two" is
// spelled out by hand here for that reason.
const laterOf = (a, b) => (a > b ? a : b);
const earlierOf = (a, b) => (a < b ? a : b);

// A month axis label ("2026-03") contributes a date range, clamped to the
// period the grid was actually built from -- the pivot is already
// period-filtered, so an unclamped month could claim rows outside the
// window the grid covers.
function monthAxisBounds(label, periodStart, periodEnd) {
  const bounds = periodBounds("month", `${label}-01`);
  return {
    start_date: laterOf(bounds.startDate, periodStart),
    end_date: earlierOf(bounds.endDate, periodEnd),
  };
}

// Builds the drawer's title/params/expectedHours from up to two axis
// labels (a total passes only one) plus the measure's value for that
// cell. `rowIds`/`colIds` are the row_ids/col_ids maps from the pivot
// response; `id ?? label` is BreakdownPanel's placeholder convention --
// filtered_entries reads a placeholder label string as "this field is
// empty".
function drawerConfig({
  rowDim, rowLabel, rowIds, colDim, colLabel, colIds,
  measure, value, periodStart, periodEnd,
}) {
  const params = {};
  let monthBounds = null;

  if (rowLabel != null) {
    if (rowDim === "month") {
      monthBounds = monthAxisBounds(rowLabel, periodStart, periodEnd);
    } else {
      params[FILTER_FOR[rowDim]] = rowIds ? (rowIds[rowLabel] ?? rowLabel) : rowLabel;
    }
  }
  if (colLabel != null) {
    if (colDim === "month") {
      monthBounds = monthAxisBounds(colLabel, periodStart, periodEnd);
    } else {
      params[FILTER_FOR[colDim]] = colIds ? (colIds[colLabel] ?? colLabel) : colLabel;
    }
  }

  const bounds = monthBounds || { start_date: periodStart, end_date: periodEnd };
  params.start_date = bounds.start_date;
  params.end_date = bounds.end_date;

  // money_sum() (views_analytics.py) sums hours * applied_rate filtered to
  // billable_code="BILLABLE" -- an "amount" cell's figure only ever came
  // from billable rows, so its drill-down must narrow to the same rows or
  // it lists non-billable time that contributed nothing to the number
  // that was clicked. TeamTab.jsx does the same for its own Amount cell.
  if (measure === "amount") params.billable = "BILLABLE";

  const title = [rowLabel, colLabel].filter((x) => x != null).join(" — ");
  return {
    title,
    params,
    // There is no hours figure to compare a non-hours measure against --
    // comparing against zero would fire the drawer's mismatch warning on
    // every click.
    expectedHours: measure === "hours" ? value : null,
  };
}

// Transparent up to a maximum alpha of ~0.45, so dark text stays readable
// at full intensity. A zero or missing cell gets no tint at all --
// Number("0.00") is falsy, same as Number(undefined).
function heatBackground(value, max) {
  // A correcting entry can make an hours/amount figure negative.
  // Clamped at zero explicitly rather than relying on a negative alpha
  // being silently ignored by the browser -- a correcting entry should
  // render exactly like any other zero, not like an unstated edge case.
  const v = Math.max(0, Number(value) || 0);
  if (!v || !max) return undefined;
  const alpha = Math.min(0.45, (v / max) * 0.45);
  return `rgba(59, 130, 246, ${alpha.toFixed(3)})`;
}

export default function ExploreTab({ filters = {} }) {
  const [rows, setRows] = useState("time_code");
  const [cols, setCols] = useState("employee");
  const [measure, setMeasure] = useState("hours");
  const [drawer, setDrawer] = useState(null);

  const { data, loading, error } = useAnalytics("pivot/", {
    ...toQuery(filters), rows, cols, measure,
  });

  const max = data ? data.rows.reduce((m, r) => data.cols.reduce((mm, c) => {
    const v = Number(data.cells[r] && data.cells[r][c]);
    return Number.isFinite(v) && v > mm ? v : mm;
  }, m), 0) : 0;

  const openDrawer = (rowLabel, colLabel, value) => {
    if (!data) return;
    setDrawer(drawerConfig({
      rowDim: rows, rowLabel, rowIds: data.row_ids,
      colDim: cols, colLabel, colIds: data.col_ids,
      measure, value,
      periodStart: filters.startDate, periodEnd: filters.endDate,
    }));
  };

  const select = (id, label, value, onChange, options) => (
    <label htmlFor={id} style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
      {label}{" "}
      <select id={id} value={value}
              onChange={(e) => onChange(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 6,
                       border: "1px solid #e5e7eb", fontSize: 13 }}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div style={{ background: "white", borderRadius: 12, padding: 24,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
      <h3 style={{ marginTop: 0, marginBottom: 16 }}>Explore</h3>

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap",
                    marginBottom: 20 }}>
        {select("pivot-rows", "Rows", rows, setRows, DIMENSIONS)}
        {select("pivot-cols", "Columns", cols, setCols, DIMENSIONS)}
        {select("pivot-measure", "Measure", measure, setMeasure, MEASURES)}
      </div>

      {loading && <div style={{ color: "#6b7280" }}>Loading…</div>}
      {error && <div style={{ color: "#b91c1c" }}>{error}</div>}

      {!loading && !error && data && (
        data.rows.length === 0 ? (
          <div style={{ color: "#6b7280" }}>
            No time matches the current filters.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse",
                            fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ ...head, textAlign: "left" }} />
                  {data.cols.map((c) => <th key={c} style={head}>{c}</th>)}
                  <th style={head}>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r}>
                    <td style={{ ...cell, textAlign: "left", fontWeight: 600 }}>
                      {r}
                    </td>
                    {data.cols.map((c) => {
                      const value = data.cells[r] && data.cells[r][c];
                      const unassigned =
                        isUnassignedEmployee(rows, r, data.row_ids) ||
                        isUnassignedEmployee(cols, c, data.col_ids);
                      return (
                        <td key={c} style={{
                          ...cell, background: heatBackground(value, max),
                        }}>
                          {value != null && (unassigned ? (
                            value
                          ) : (
                            <button
                              type="button" style={cellButton}
                              aria-label={`${r} — ${c}: ${value} — show entries`}
                              onClick={() => openDrawer(r, c, value)}
                            >
                              {value}
                            </button>
                          ))}
                        </td>
                      );
                    })}
                    <td style={{ ...cell, fontWeight: 600 }}>
                      {isUnassignedEmployee(rows, r, data.row_ids) ? (
                        data.row_totals[r]
                      ) : (
                        <button
                          type="button" style={cellButton}
                          aria-label={`${r} total: ${data.row_totals[r]} — show entries`}
                          onClick={() => openDrawer(r, null, data.row_totals[r])}
                        >
                          {data.row_totals[r]}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 600 }}>
                  <td style={{ ...cell, textAlign: "left",
                               borderTop: "2px solid #e5e7eb" }}>Total</td>
                  {data.cols.map((c) => (
                    <td key={c} style={{ ...cell, borderTop: "2px solid #e5e7eb" }}>
                      {isUnassignedEmployee(cols, c, data.col_ids) ? (
                        data.col_totals[c]
                      ) : (
                        <button
                          type="button" style={cellButton}
                          aria-label={`${c} total: ${data.col_totals[c]} — show entries`}
                          onClick={() => openDrawer(null, c, data.col_totals[c])}
                        >
                          {data.col_totals[c]}
                        </button>
                      )}
                    </td>
                  ))}
                  {/* The grand total is the unfiltered period, which the
                      breakdown panel already shows -- left alone on
                      purpose, not an oversight. */}
                  <td style={{ ...cell, borderTop: "2px solid #e5e7eb" }}>
                    {data.grand_total}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}

      <EntriesDrawer
        open={Boolean(drawer)}
        title={drawer ? drawer.title : ""}
        params={drawer ? drawer.params : {}}
        expectedHours={drawer ? drawer.expectedHours : null}
        onClose={() => setDrawer(null)}
      />
    </div>
  );
}
